import { z } from 'zod'
import { treeDtoSchema, type TreeListDto } from '@lib/dto/tree.dto'
import type { VisitKind } from '@lib/enums/visit-kind.enum'
import { uuidSchema } from '@lib/validators/uuid.validator'
import {
  irrigationProblemDtoSchema,
  type IrrigationProblemDto,
} from '@lib/dto/irrigation.dto'
import type { TreeProblemDto } from '@lib/dto/tree-problem.dto'
import type { TreeProblemTypeDto } from '@lib/dto/tree-problem-type.dto'
import logger, { toErrorMessage } from '@/helpers/logger'

/*
 * "Last visit" on a revisit: what the visit before this one found, shown for
 * reference, and "Still here", which brings one of its problems into this
 * visit (docs/site-visits.md). Nothing comes forward on its own: a new visit
 * starts clean.
 *
 * Kept on the phone as well as read from the server, since a revisit is a
 * day in a park, and a sheet that lost last year's findings with the signal
 * would leave the technician guessing what they came back to check.
 */

/** What last visit found on one tree: its findings, without the tree. */
export const lastVisitTreeSchema = treeDtoSchema.pick({
  id: true,
  problems: true,
  jobs: true,
  estimatedLaborMinutes: true,
  removalOffered: true,
  removalMinutes: true,
  removalNotes: true,
  isOptional: true,
  noPruningRecommended: true,
})

export type LastVisitTree = z.infer<typeof lastVisitTreeSchema>

/** Whether a visit found anything on a tree at all. */
function foundAnything(tree: LastVisitTree): boolean {
  return (
    tree.problems.length > 0 ||
    tree.jobs.length > 0 ||
    tree.estimatedLaborMinutes !== null ||
    tree.removalOffered ||
    tree.isOptional ||
    tree.noPruningRecommended
  )
}

/** The trees a visit found something on, trimmed to what it found. */
export function treeFindings(list: TreeListDto): LastVisitTree[] {
  return list.trees
    .map((tree) => ({
      id: tree.id,
      problems: tree.problems,
      jobs: tree.jobs,
      estimatedLaborMinutes: tree.estimatedLaborMinutes,
      removalOffered: tree.removalOffered,
      removalMinutes: tree.removalMinutes,
      removalNotes: tree.removalNotes,
      isOptional: tree.isOptional,
      noPruningRecommended: tree.noPruningRecommended,
    }))
    .filter(foundAnything)
}

/* -- kept on the phone ------------------------------------------------- */

const storedTreesSchema = z.object({
  visitId: uuidSchema,
  trees: z.array(lastVisitTreeSchema),
})

const storedProblemsSchema = z.object({
  visitId: uuidSchema,
  problems: z.array(irrigationProblemDtoSchema),
})

export type StoredLastVisitTrees = z.infer<typeof storedTreesSchema>
export type StoredLastVisitProblems = z.infer<typeof storedProblemsSchema>

function storageKey(siteId: string, kind: VisitKind): string {
  return `swocrm.last-visit.${kind}.${siteId}.v1`
}

/** The stored copy, or null for none, one unreadable, or storage refused. */
function read<T>(
  siteId: string,
  kind: VisitKind,
  schema: z.ZodType<T>,
): T | null {
  try {
    const raw = window.localStorage.getItem(storageKey(siteId, kind))
    if (raw === null) return null
    const parsed = schema.safeParse(JSON.parse(raw))
    return parsed.success ? parsed.data : null
  } catch (caught) {
    logger.error('last visit: could not read the stored findings', {
      siteId,
      reason: toErrorMessage(caught),
    })
    return null
  }
}

function write(
  siteId: string,
  kind: VisitKind,
  value: StoredLastVisitTrees | StoredLastVisitProblems,
): void {
  try {
    window.localStorage.setItem(storageKey(siteId, kind), JSON.stringify(value))
  } catch (caught) {
    // Only a copy: the sheet reads the server's when it has a signal.
    logger.error('last visit: could not keep the findings', {
      siteId,
      reason: toErrorMessage(caught),
    })
  }
}

export function loadLastVisitTrees(
  siteId: string,
): StoredLastVisitTrees | null {
  return read(siteId, 'trees', storedTreesSchema)
}

export function saveLastVisitTrees(
  siteId: string,
  value: StoredLastVisitTrees,
): void {
  write(siteId, 'trees', value)
}

export function loadLastVisitProblems(
  siteId: string,
): StoredLastVisitProblems | null {
  return read(siteId, 'irrigation', storedProblemsSchema)
}

export function saveLastVisitProblems(
  siteId: string,
  value: StoredLastVisitProblems,
): void {
  write(siteId, 'irrigation', value)
}

/* -- Still here --------------------------------------------------------- */

/**
 * Where one of last visit's problems stands on this visit: brought forward
 * already, able to be, of a kind taken out of the directory since, which
 * the server would refuse (and a refused change fails the phone's whole
 * queue on every retry), or of a kind the phone cannot check, because the
 * directory has not loaded (a cold start with no signal). Kinds are retired,
 * never deleted, so one missing from a loaded directory cannot happen.
 */
export type StillHereState = 'brought' | 'available' | 'retired' | 'unchecked'

export function treeProblemState(
  problem: TreeProblemDto,
  current: readonly TreeProblemDto[],
  typesById: ReadonlyMap<string, TreeProblemTypeDto>,
): StillHereState {
  if (current.some((found) => found.previousId === problem.id)) {
    return 'brought'
  }
  if (problem.typeId === null) return 'available'
  // One of its kind already recorded this visit can always be linked to.
  if (current.some((found) => found.typeId === problem.typeId)) {
    return 'available'
  }
  const type = typesById.get(problem.typeId)
  if (type === undefined) return 'unchecked'
  return type.archivedAt === null ? 'available' : 'retired'
}

/**
 * This visit's problems on a tree with last visit's `problem` brought
 * forward: its kind, notes and treatments copied in, linked to it, for this
 * visit to rate and photograph. A tree carries one of each catalogued kind,
 * so one already recorded this visit is linked rather than repeated.
 * Treatments taken out of the directory since are left behind, as the
 * server would refuse them.
 */
export function bringTreeProblemForward(
  current: readonly TreeProblemDto[],
  problem: TreeProblemDto,
  typesById: ReadonlyMap<string, TreeProblemTypeDto>,
  now: string = new Date().toISOString(),
): TreeProblemDto[] {
  const sameKind =
    problem.typeId === null
      ? undefined
      : current.find((found) => found.typeId === problem.typeId)
  if (sameKind !== undefined) {
    return current.map((found) =>
      found.id === sameKind.id && found.previousId === null
        ? { ...found, previousId: problem.id, updatedAt: now }
        : found,
    )
  }
  const live = new Set(
    (problem.typeId === null
      ? []
      : (typesById.get(problem.typeId)?.solutions ?? [])
    )
      .filter((solution) => solution.archivedAt === null)
      .map((solution) => solution.id),
  )
  return [
    ...current,
    {
      id: crypto.randomUUID(),
      typeId: problem.typeId,
      notes: problem.notes,
      severity: null,
      // When it is dealt with, like its treatments' seasons below.
      season: problem.season,
      solutions: problem.solutions
        .filter((solution) => live.has(solution.solutionId))
        .map((solution) => ({ ...solution, id: crypto.randomUUID() })),
      previousId: problem.id,
      // Not the price set last time: it was that visit's judgment of that
      // visit's work, and this one is priced afresh.
      priceOverride: null,
      createdAt: now,
      updatedAt: now,
    },
  ]
}

/** Whether one of last visit's irrigation problems was brought forward. */
export function isBroughtForward(
  problem: IrrigationProblemDto,
  current: readonly IrrigationProblemDto[],
): boolean {
  return current.some((found) => found.previousId === problem.id)
}

/**
 * Last visit's irrigation problem, brought into this visit: its kind, where
 * it is, how many, its notes and its repair, linked to it. Severity, photos
 * and who found it are this visit's to record.
 */
export function bringIrrigationProblemForward(
  problem: IrrigationProblemDto,
  now: string = new Date().toISOString(),
): IrrigationProblemDto {
  return {
    ...problem,
    id: crypto.randomUUID(),
    severity: null,
    fixedAt: null,
    foundByName: null,
    previousId: problem.id,
    createdAt: now,
    updatedAt: now,
  }
}
